import ExcelJS from 'exceljs'
import type {
  TimesheetReportResponse,
  TimesheetEmployee,
  TimesheetDay,
} from '../types/reports.types'

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']

// Colors matching the UI
const COLORS = {
  green: 'DCFCE7',      // bg-green-100 - отработано
  orange: 'FFEDD5',     // bg-orange-100 - опоздание
  red: 'FEF2F2',        // bg-red-50 - неявка
  gray: 'F3F4F6',       // bg-gray-100 - выходной
  greenHeader: 'DCFCE7', // bg-green-50 - Часов
  blueHeader: 'DBEAFE',  // bg-blue-50 - Норма
  grayHeader: 'F3F4F6',  // bg-gray-100 - +/-
  redText: 'DC2626',     // text-red-600
  greenText: '16A34A',   // text-green-600
  weekendText: 'EF4444', // text-red-500 for weekend headers
  orgHeader: 'E2E8F0',   // bg-slate-100 - organization header
}

interface OrganizationGroup {
  organizationName: string
  employees: TimesheetEmployee[]
}

type CellStyle = 'normal' | 'late' | 'absent' | 'weekend' | 'orgHeader'

function getCellStyle(day: TimesheetDay | undefined): CellStyle {
  if (!day) return 'normal'
  if (!day.is_working_day) return 'weekend'
  if (!day.check_in) return 'absent'
  if (day.is_late) return 'late'
  return 'normal'
}

function formatWorkedTime(day: TimesheetDay | undefined): string {
  if (!day) return '-'
  if (!day.is_working_day) return 'В'
  if (!day.check_in) return 'Н'

  const hours = Math.floor(day.worked_minutes / 60)
  const minutes = day.worked_minutes % 60

  if (minutes === 0) return `${hours}`
  return `${hours}:${String(minutes).padStart(2, '0')}`
}

function formatDelta(delta: number): string {
  if (delta === 0) return '0'
  return delta > 0 ? `+${delta}` : `${delta}`
}

function groupByOrganization(employees: TimesheetEmployee[]): OrganizationGroup[] {
  const groups = new Map<string | null, TimesheetEmployee[]>()

  for (const emp of employees) {
    const key = emp.organization_id
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key)!.push(emp)
  }

  return Array.from(groups.entries()).map(([, emps]) => ({
    organizationName: emps[0]?.organization_name || 'Без организации',
    employees: emps,
  }))
}

function generateDates(year: number, month: number, daysInMonth: number): Date[] {
  const result: Date[] = []
  for (let day = 1; day <= daysInMonth; day++) {
    result.push(new Date(year, month - 1, day))
  }
  return result
}

function formatDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function isWeekend(date: Date): boolean {
  const day = date.getDay()
  return day === 0 || day === 6
}

function applyDayCellStyle(cell: ExcelJS.Cell, style: CellStyle): void {
  cell.alignment = { horizontal: 'center', vertical: 'middle' }
  cell.border = {
    top: { style: 'thin', color: { argb: 'D1D5DB' } },
    left: { style: 'thin', color: { argb: 'D1D5DB' } },
    bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
    right: { style: 'thin', color: { argb: 'D1D5DB' } },
  }

  switch (style) {
    case 'normal':
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: COLORS.green },
      }
      break
    case 'late':
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: COLORS.orange },
      }
      break
    case 'absent':
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: COLORS.red },
      }
      cell.font = { color: { argb: COLORS.redText }, bold: true }
      break
    case 'weekend':
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: COLORS.gray },
      }
      break
    case 'orgHeader':
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: COLORS.orgHeader },
      }
      cell.font = { bold: true }
      break
  }
}

export async function exportTimesheetToExcel(report: TimesheetReportResponse): Promise<void> {
  const { employees, meta } = report
  const dates = generateDates(meta.year, meta.month, meta.days_in_month)
  const dateStrings = dates.map(formatDateString)
  const organizationGroups = groupByOrganization(employees)
  const hasMultipleOrgs = organizationGroups.length > 1

  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'TCO Admin'
  workbook.created = new Date()

  const worksheet = workbook.addWorksheet('Табель', {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }],
  })

  // Calculate total columns: 1 (name) + days + 3 (summary)
  const totalColumns = 1 + dates.length + 3

  // Title row
  const titleRow = worksheet.addRow([`Табель учёта рабочего времени - ${meta.month_name} ${meta.year}`])
  worksheet.mergeCells(1, 1, 1, Math.min(15, totalColumns))
  titleRow.font = { size: 14, bold: true }
  titleRow.height = 25

  // Header row 1: Weekday names
  const headerRow1Data: string[] = ['Сотрудник']
  for (const date of dates) {
    headerRow1Data.push(WEEKDAY_NAMES[date.getDay()])
  }
  headerRow1Data.push('Часов', 'Норма', '+/-')
  const headerRow1 = worksheet.addRow(headerRow1Data)
  headerRow1.font = { bold: true, size: 9 }
  headerRow1.height = 20

  // Style header row 1
  headerRow1.eachCell((cell, colNumber) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'F3F4F6' },
    }

    // Color weekend day names in red
    if (colNumber > 1 && colNumber <= dates.length + 1) {
      const dateIndex = colNumber - 2
      if (isWeekend(dates[dateIndex])) {
        cell.font = { bold: true, size: 9, color: { argb: COLORS.weekendText } }
      }
    }

    // Summary column headers
    if (colNumber === totalColumns - 2) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.greenHeader } }
    } else if (colNumber === totalColumns - 1) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.blueHeader } }
    } else if (colNumber === totalColumns) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.grayHeader } }
    }
  })

  // Header row 2: Day numbers
  const headerRow2Data: (string | number)[] = ['Подразделение']
  for (const date of dates) {
    headerRow2Data.push(date.getDate())
  }
  headerRow2Data.push('', '', '')
  const headerRow2 = worksheet.addRow(headerRow2Data)
  headerRow2.font = { bold: true, size: 9 }
  headerRow2.height = 18

  // Style header row 2
  headerRow2.eachCell((cell, colNumber) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'F3F4F6' },
    }

    // Color weekend day numbers in red
    if (colNumber > 1 && colNumber <= dates.length + 1) {
      const dateIndex = colNumber - 2
      if (isWeekend(dates[dateIndex])) {
        cell.font = { bold: true, size: 9, color: { argb: COLORS.weekendText } }
      }
    }

    // Summary column headers
    if (colNumber === totalColumns - 2) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.greenHeader } }
    } else if (colNumber === totalColumns - 1) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.blueHeader } }
    } else if (colNumber === totalColumns) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.grayHeader } }
    }
  })

  // Data rows
  for (const group of organizationGroups) {
    // Organization header (if multiple orgs)
    if (hasMultipleOrgs) {
      const orgRowData: (string | number)[] = [group.organizationName]
      for (let i = 0; i < dates.length + 3; i++) {
        orgRowData.push('')
      }
      const orgRow = worksheet.addRow(orgRowData)
      worksheet.mergeCells(orgRow.number, 1, orgRow.number, totalColumns)
      orgRow.eachCell((cell) => {
        applyDayCellStyle(cell, 'orgHeader')
      })
      orgRow.height = 22
    }

    // Employee rows
    for (const employee of group.employees) {
      const rowData: (string | number)[] = []

      // Employee name with department
      const nameWithDept = employee.department_name
        ? `${employee.employee_name} (${employee.department_name})`
        : employee.employee_name
      rowData.push(nameWithDept)

      // Day values
      for (const dateStr of dateStrings) {
        const day = employee.days[dateStr]
        rowData.push(formatWorkedTime(day))
      }

      // Summary values
      rowData.push(employee.totals.worked_hours)
      rowData.push(employee.totals.norm_hours)
      rowData.push(formatDelta(employee.totals.delta_hours))

      const row = worksheet.addRow(rowData)
      row.height = 20

      // Style each cell
      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'D1D5DB' } },
          left: { style: 'thin', color: { argb: 'D1D5DB' } },
          bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
          right: { style: 'thin', color: { argb: 'D1D5DB' } },
        }

        if (colNumber === 1) {
          // Name column
          cell.alignment = { horizontal: 'left', vertical: 'middle' }
          cell.font = { size: 10 }
        } else if (colNumber > 1 && colNumber <= dates.length + 1) {
          // Day columns
          const dateIndex = colNumber - 2
          const dateStr = dateStrings[dateIndex]
          const day = employee.days[dateStr]
          const style = getCellStyle(day)
          applyDayCellStyle(cell, style)
          cell.font = { size: 9, ...cell.font }
        } else if (colNumber === totalColumns - 2) {
          // Часов column
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.greenHeader } }
          cell.font = { size: 10, bold: true }
        } else if (colNumber === totalColumns - 1) {
          // Норма column
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.blueHeader } }
          cell.font = { size: 10 }
        } else if (colNumber === totalColumns) {
          // +/- column
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          const delta = employee.totals.delta_hours
          if (delta < 0) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } }
            cell.font = { size: 10, bold: true, color: { argb: COLORS.redText } }
          } else if (delta > 0) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.greenHeader } }
            cell.font = { size: 10, bold: true, color: { argb: COLORS.greenText } }
          } else {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.grayHeader } }
            cell.font = { size: 10 }
          }
        }
      })
    }
  }

  // Calculate totals
  const totalWorkedHours = employees.reduce((sum, emp) => sum + emp.totals.worked_hours, 0)
  const totalNormHours = employees.reduce((sum, emp) => sum + emp.totals.norm_hours, 0)

  // Add totals row
  const totalsRowData: (string | number)[] = ['ИТОГО']
  for (let i = 0; i < dates.length; i++) {
    totalsRowData.push('')
  }
  totalsRowData.push(totalWorkedHours)
  totalsRowData.push(totalNormHours)
  totalsRowData.push('')

  const totalsRow = worksheet.addRow(totalsRowData)
  totalsRow.height = 22
  totalsRow.font = { bold: true, size: 11 }

  totalsRow.eachCell((cell, colNumber) => {
    cell.border = {
      top: { style: 'medium' },
      left: { style: 'thin' },
      bottom: { style: 'medium' },
      right: { style: 'thin' },
    }
    cell.alignment = { horizontal: colNumber === 1 ? 'left' : 'center', vertical: 'middle' }

    if (colNumber === totalColumns - 2) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.greenHeader } }
    } else if (colNumber === totalColumns - 1) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.blueHeader } }
    } else if (colNumber === totalColumns) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.grayHeader } }
    }
  })

  // Set column widths
  worksheet.getColumn(1).width = 35 // Employee name
  for (let i = 2; i <= dates.length + 1; i++) {
    worksheet.getColumn(i).width = 5 // Day columns
  }
  worksheet.getColumn(dates.length + 2).width = 8 // Часов
  worksheet.getColumn(dates.length + 3).width = 8 // Норма
  worksheet.getColumn(dates.length + 4).width = 8 // +/-

  // Add legend at the bottom
  worksheet.addRow([])
  const legendRowNum = worksheet.rowCount

  const legendItems = [
    { text: 'Отработано', color: COLORS.green },
    { text: 'Опоздание', color: COLORS.orange },
    { text: 'Неявка', color: COLORS.red },
    { text: 'Выходной', color: COLORS.gray },
  ]

  let legendCol = 1
  for (const item of legendItems) {
    const cell = worksheet.getCell(legendRowNum + 1, legendCol)
    cell.value = item.text
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: item.color } }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
    cell.alignment = { horizontal: 'center' }
    cell.font = { size: 9 }
    legendCol += 2
  }

  // Generate and download file
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const filename = `Табель_${meta.month_name}_${meta.year}.xlsx`
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}
